import { useState, useRef, useEffect } from 'react'
import type { KeyboardEvent } from 'react'
import { Terminal as TerminalIcon, X } from 'lucide-react'
import { useFileStore } from '../stores/fileStore'
import { useAuthStore } from '../stores/authStore'
import { api } from '../utils/api'
import { publicFileUrl } from '../config'
import type { FileSystemItem } from '@shared'

interface Props {
  isOpen: boolean
  onClose: () => void
}

type LineKind = 'in' | 'out' | 'err'
interface Line { kind: LineKind; text: string }

const HELP = [
  'Commands:',
  '  ls                 list current folder',
  '  pwd                print working directory',
  '  cd <path>          change directory  (.. and / supported)',
  '  mkdir <name>       create folder',
  '  touch <name>       create text file',
  '  rm <name>          delete to trash (restorable)',
  '  mv <name> <dir>    move item into a folder',
  '  rename <a> <b>     rename item',
  '  find <query>       search files',
  '  open <name>        open file URL in a new tab',
  '  url <name>         copy public URL to clipboard',
  '  wget <url> [name]  download a remote file into here',
  '  upload             pick + upload a file into here',
  '  clear              clear the screen',
  '  help               this list',
].join('\n')

// Resolve `arg` against `cwd`, supporting absolute (/…), relative, `.` and `..`.
// Returns a clean path with no leading slash ('' == root).
function resolvePath(cwd: string, arg: string): string {
  const segments = arg.startsWith('/') ? [] : cwd.split('/').filter(Boolean)
  for (const part of arg.split('/')) {
    if (part === '' || part === '.') continue
    if (part === '..') segments.pop()
    else segments.push(part)
  }
  return segments.join('/')
}

// Sentinels let the pure command runner request a side effect the component owns.
const CLEAR = ' CLEAR'
const UPLOAD = ' UPLOAD'

// Run one command line. Pure w.r.t. React — reads/writes the stores via getState
// so there are no stale closures, and returns lines to print (or a sentinel).
async function execute(raw: string): Promise<Line[]> {
  const line = raw.trim()
  if (!line) return []

  const [cmd, ...args] = line.split(/\s+/)
  const st = useFileStore.getState()
  const cwd = st.currentPath
  const items = st.items
  const findItem = (name?: string): FileSystemItem | undefined =>
    name ? items.find(i => i.item.name === name) : undefined

  const out: Line[] = []
  try {
    switch (cmd) {
      case 'help':
        out.push({ kind: 'out', text: HELP })
        break

      case 'clear':
        return [{ kind: 'out', text: CLEAR }]

      case 'pwd':
        out.push({ kind: 'out', text: '/' + cwd })
        break

      case 'ls':
        if (items.length === 0) { out.push({ kind: 'out', text: '(empty)' }); break }
        for (const it of items) {
          out.push({ kind: 'out', text: `${it.type === 'folder' ? '📁' : '📄'} ${it.item.name}` })
        }
        break

      case 'cd': {
        const target = resolvePath(cwd, args[0] ?? '')
        await st.navigateToFolder(target)
        // loadItems only commits currentPath on success — mismatch means scope/404.
        if (useFileStore.getState().currentPath !== target) {
          out.push({ kind: 'err', text: `cd: cannot access '${args[0] ?? ''}'` })
        }
        break
      }

      case 'mkdir':
        if (!args[0]) { out.push({ kind: 'err', text: 'mkdir: missing folder name' }); break }
        await st.createFolder(args[0], true)
        break

      case 'touch': {
        if (!args[0]) { out.push({ kind: 'err', text: 'touch: missing file name' }); break }
        const { token, csrfToken } = useAuthStore.getState()
        const res = await api.post('/api/files/create', {
          filename: args[0], parentPath: cwd, isPublic: false,
        }, token || '', csrfToken || undefined)
        if (res.success) { await st.loadItems(); out.push({ kind: 'out', text: `created ${args[0]}` }) }
        else out.push({ kind: 'err', text: res.error || 'touch failed' })
        break
      }

      case 'rm': {
        const it = findItem(args[0])
        if (!it) { out.push({ kind: 'err', text: `rm: '${args[0] ?? ''}' not found here` }); break }
        await st.deleteItem(it.item.id, it.type)
        out.push({ kind: 'out', text: `removed ${args[0]} → trash` })
        break
      }

      case 'mv': {
        const it = findItem(args[0])
        if (!it) { out.push({ kind: 'err', text: `mv: '${args[0] ?? ''}' not found here` }); break }
        if (!args[1]) { out.push({ kind: 'err', text: 'mv: missing destination folder' }); break }
        const dest = resolvePath(cwd, args[1])
        await st.moveItems([{ id: it.item.id, type: it.type }], dest)
        out.push({ kind: 'out', text: `moved ${args[0]} → /${dest}` })
        break
      }

      case 'rename': {
        const it = findItem(args[0])
        if (!it) { out.push({ kind: 'err', text: `rename: '${args[0] ?? ''}' not found here` }); break }
        if (!args[1]) { out.push({ kind: 'err', text: 'rename: missing new name' }); break }
        await st.renameItem(it.item.id, args[1], it.type)
        out.push({ kind: 'out', text: `renamed ${args[0]} → ${args[1]}` })
        break
      }

      case 'find': {
        const q = args.join(' ')
        if (!q) { out.push({ kind: 'err', text: 'find: missing query' }); break }
        const { token, csrfToken } = useAuthStore.getState()
        const res = await api.get<{ results?: FileSystemItem[] }>(
          `/api/search?q=${encodeURIComponent(q)}`, token || '', csrfToken || undefined)
        const results = res.data?.results ?? []
        if (results.length === 0) out.push({ kind: 'out', text: 'no matches' })
        else results.forEach(r => out.push({ kind: 'out', text: `${r.type === 'folder' ? '📁' : '📄'} /${r.item.path}` }))
        break
      }

      case 'open':
      case 'url': {
        const it = findItem(args[0])
        if (!it || it.type !== 'file') { out.push({ kind: 'err', text: `${cmd}: file '${args[0] ?? ''}' not found here` }); break }
        const u = publicFileUrl(it.item.path)
        if (cmd === 'open') {
          window.open(u, '_blank', 'noopener')
          out.push({ kind: 'out', text: `opening ${u}` })
        } else {
          try { await navigator.clipboard.writeText(u) } catch { /* clipboard may be blocked */ }
          out.push({ kind: 'out', text: `copied ${u}` })
        }
        break
      }

      case 'wget': {
        const src = args[0]
        if (!src) { out.push({ kind: 'err', text: 'wget: missing URL' }); break }
        const { token, csrfToken } = useAuthStore.getState()
        const res = await api.post<{ file?: { name: string; path: string; size: number } }>(
          '/api/files/fetch-url',
          { url: src, parentPath: cwd, fileName: args[1] || undefined },
          token || '', csrfToken || undefined,
        )
        if (res.success && res.data?.file) {
          await st.loadItems()
          const f = res.data.file
          out.push({ kind: 'out', text: `saved ${f.name} (${(f.size / 1024).toFixed(1)} KB) → /${f.path}` })
        } else {
          out.push({ kind: 'err', text: res.error || 'wget failed' })
        }
        break
      }

      case 'upload':
        return [{ kind: 'out', text: UPLOAD }]

      default:
        out.push({ kind: 'err', text: `command not found: ${cmd} (try 'help')` })
    }
  } catch (e) {
    out.push({ kind: 'err', text: e instanceof Error ? e.message : 'command failed' })
  }
  return out
}

// A separate, VS Code-style docked terminal for the file manager. Commands run
// against the SAME store the GUI uses (currentPath/items), so the two stay in
// perfect sync — `cd` here moves the GUI too, and GUI navigation updates the prompt.
export function FileTerminal({ isOpen, onClose }: Props) {
  const currentPath = useFileStore(s => s.currentPath)
  const uploadFile = useFileStore(s => s.uploadFile)
  const role = useAuthStore(s => s.role)

  const [lines, setLines] = useState<Line[]>([
    { kind: 'out', text: "Vendetta CLI — type 'help' for commands." },
  ])
  const [input, setInput] = useState('')
  const [history, setHistory] = useState<string[]>([])
  const [histIdx, setHistIdx] = useState(-1)
  const [busy, setBusy] = useState(false)

  const bodyRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // Keep the newest output in view + focus the prompt whenever the panel opens.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight
  }, [lines])
  useEffect(() => {
    if (isOpen) inputRef.current?.focus()
  }, [isOpen])

  const prompt = `${role ?? 'user'}@vndta:/${currentPath}$`

  const submit = async () => {
    const raw = input
    setInput('')
    setLines(prev => [...prev, { kind: 'in', text: `${prompt} ${raw}` }])
    if (raw.trim()) setHistory(prev => [...prev, raw])
    setHistIdx(-1)

    setBusy(true)
    const result = await execute(raw)
    setBusy(false)
    // Keep the caret in the terminal after Enter — the prompt should never lose
    // focus just because a command ran.
    inputRef.current?.focus()

    if (result.length === 1 && result[0].text === CLEAR) { setLines([]); return }
    if (result.length === 1 && result[0].text === UPLOAD) {
      setLines(prev => [...prev, { kind: 'out', text: 'select a file…' }])
      fileRef.current?.click()
      return
    }
    if (result.length) setLines(prev => [...prev, ...result])
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); if (!busy) submit(); return }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!history.length) return
      const idx = histIdx < 0 ? history.length - 1 : Math.max(0, histIdx - 1)
      setHistIdx(idx); setInput(history[idx])
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (histIdx < 0) return
      const idx = histIdx + 1
      if (idx >= history.length) { setHistIdx(-1); setInput('') }
      else { setHistIdx(idx); setInput(history[idx]) }
    }
  }

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setBusy(true)
    setLines(prev => [...prev, { kind: 'out', text: `uploading ${file.name}…` }])
    try {
      await uploadFile(file, true)
      setLines(prev => [...prev, { kind: 'out', text: `uploaded ${file.name}` }])
    } catch (err) {
      setLines(prev => [...prev, { kind: 'err', text: err instanceof Error ? err.message : 'upload failed' }])
    } finally {
      setBusy(false)
      inputRef.current?.focus()
    }
  }

  if (!isOpen) return null

  const tone: Record<LineKind, string> = {
    in: 'text-emerald-400',
    out: 'text-dark-200',
    err: 'text-red-400',
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 h-[45vh] border-t border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl flex flex-col">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <div className="flex items-center gap-2 text-dark-200">
          <TerminalIcon className="h-4 w-4 text-emerald-400" />
          <span className="text-xs font-semibold uppercase tracking-wider">Terminal</span>
          <span className="text-[10px] text-dark-500">Ctrl+` to toggle · Esc to close</span>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-dark-400 transition hover:bg-white/10 hover:text-white"
          title="Close terminal"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={bodyRef}
        className="flex-1 overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed"
        onClick={() => inputRef.current?.focus()}
      >
        {lines.map((l, i) => (
          <pre key={i} className={`whitespace-pre-wrap break-words ${tone[l.kind]}`}>{l.text}</pre>
        ))}
      </div>

      <div className="flex items-center gap-2 border-t border-white/10 px-4 py-2 font-mono text-xs">
        <span className="shrink-0 text-emerald-400">{prompt}</span>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          className="flex-1 bg-transparent text-white outline-none placeholder-dark-600 disabled:opacity-100"
          placeholder={busy ? 'running…' : "type a command, 'help' for list"}
          readOnly={busy}
        />
      </div>

      <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
    </div>
  )
}
